import {useCallback, useEffect, useMemo, useRef, useState} from 'react';

import {CountryCode, parsePhoneNumberFromString} from 'libphonenumber-js';
import {
  CountryIso2,
  defaultCountries,
  guessCountryByPartialPhoneNumber,
  parseCountry,
  usePhoneInput,
} from 'react-international-phone';

import {useSkipPhoneValidation} from '@/hooks';

import {
  CLIPBOARD_TEXT_FORMAT,
  INVALID_PHONE_NUMBER_ERROR,
  LEADING_DIAL_CODE_REGEX,
} from './constants';
import {formatPhoneNumber, isSpecialNumberAllowed} from './utils';

interface UseActionsParams {
  setPhoneNumber?: string;
  defaultCountry?: CountryIso2;
  countries?: CountryIso2[];
  onChange?: (isValid: boolean, phone: string) => void;
  customError?: string;
}

export const useActions = ({
  setPhoneNumber = '',
  defaultCountry,
  countries,
  onChange,
  customError,
}: UseActionsParams) => {
  //#region [---- [PROPERTIES] ----]
  const allowedCountries = useMemo(
    () =>
      !countries
        ? defaultCountries
        : defaultCountries.filter(country => {
            const {iso2} = parseCountry(country);
            return countries.includes(iso2);
          }),
    [countries],
  );
  const {isSkipPhoneValidationEnabled} = useSkipPhoneValidation();
  //#endregion

  //#region [---- [HOOKS] ----]
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [isValid, setIsValid] = useState<boolean>(true);
  const [phoneValue, setPhoneValue] = useState(setPhoneNumber || '');
  // usePhoneInput needs an onChange before `country` exists, but the validator
  // below needs `country` to be memoized correctly. This ref breaks that
  // circular dependency so onChange always calls the latest validator.
  const validateAndHandlePhoneNumberRef =
    useRef<(phoneNumber: string, countryIso2?: string) => void>();
  //#endregion

  //#region [---- [LOGIC] ----]
  const {inputRef, country, setCountry} = usePhoneInput({
    defaultCountry,
    value: setPhoneNumber,
    countries: allowedCountries,
    onChange: data => {
      validateAndHandlePhoneNumberRef.current?.(data.phone, country?.iso2);
    },
  });

  const validateAndHandlePhoneNumber = useCallback(
    (phoneNumber: string, countryIso2?: string) => {
      const phoneWithoutDialCode = phoneNumber?.replace(
        `+${country?.dialCode}`,
        '',
      );
      if (!phoneWithoutDialCode || phoneWithoutDialCode?.length === 0) {
        setIsValid(true);
        setErrorMessage('');
        onChange?.(true, '');
        return;
      }
      const countryCode = countryIso2?.toUpperCase() as CountryCode;
      const parsedNumber = parsePhoneNumberFromString(phoneNumber, countryCode);
      // Dev Options can disable phone validity checks (e.g. to allow fake
      // numbers created for local/testing environments) without touching prod.
      if (
        isSkipPhoneValidationEnabled ||
        isSpecialNumberAllowed(phoneNumber) ||
        (parsedNumber && parsedNumber.isValid())
      ) {
        if (!!customError) {
          setIsValid(false);
          onChange?.(false, phoneNumber);
          setErrorMessage(customError);
        } else {
          setIsValid(true);
          setErrorMessage('');
          onChange?.(true, phoneNumber);
        }
      } else if (phoneNumber && phoneNumber?.length > 0) {
        setIsValid(false);
        onChange?.(false, phoneNumber);

        setErrorMessage(INVALID_PHONE_NUMBER_ERROR);
      }
    },
    [country?.dialCode, customError, isSkipPhoneValidationEnabled, onChange],
  );

  // Assigned synchronously during render (not in a useEffect) so the ref is
  // always current even if usePhoneInput's onChange fires before effects run.
  validateAndHandlePhoneNumberRef.current = validateAndHandlePhoneNumber;

  const handlePhoneValueUpdate = useCallback(
    (rawValue: string) => {
      const formattedValue = formatPhoneNumber(
        rawValue,
        country?.dialCode || '',
      );
      let matchedCountryIso2 = country?.iso2;

      // A value carrying its own "+" dial code (e.g. a full international
      // number pasted in) may belong to a different country than the one
      // currently selected — detect it so the flag/dial code shown stays in
      // sync with what the user actually entered.
      if (rawValue?.trim().startsWith('+')) {
        const guess = guessCountryByPartialPhoneNumber({
          phone: formattedValue,
          countries: allowedCountries,
          currentCountryIso2: country?.iso2,
        });
        if (guess.fullDialCodeMatch && guess.country) {
          matchedCountryIso2 = guess.country.iso2;
          if (guess.country.iso2 !== country?.iso2) {
            setCountry(guess.country.iso2);
          }
        }
      }

      setPhoneValue(formattedValue);
      validateAndHandlePhoneNumber(formattedValue, matchedCountryIso2);
    },
    [allowedCountries, country, setCountry, validateAndHandlePhoneNumber],
  );

  const handlePaste = useCallback(
    (event: React.ClipboardEvent<HTMLInputElement>) => {
      event.preventDefault();
      const pastedData = event?.clipboardData?.getData(CLIPBOARD_TEXT_FORMAT);
      handlePhoneValueUpdate(pastedData);
    },
    [handlePhoneValueUpdate],
  );

  const handleDisplayChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      handlePhoneValueUpdate(event.target.value);
    },
    [handlePhoneValueUpdate],
  );
  //#endregion

  //#region [---- [EFFECTS] ----]
  useEffect(() => {
    if (setPhoneNumber) {
      setPhoneValue(setPhoneNumber);
    }
  }, [setPhoneNumber]);

  useEffect(() => {
    if (defaultCountry) {
      const defaultDialCode = allowedCountries
        .map(country => parseCountry(country))
        .find(parsedCountry => parsedCountry.iso2 === defaultCountry)?.dialCode;
      if (defaultDialCode) {
        setPhoneValue(prev => {
          if (!prev?.startsWith(`+${defaultDialCode}`)) {
            return `+${defaultDialCode}${prev.replace(LEADING_DIAL_CODE_REGEX, '')}`;
          }
          return prev;
        });
      }
    }
  }, [defaultCountry, allowedCountries]);
  //#endregion

  return {
    inputRef,
    country,
    setCountry,
    allowedCountries,
    errorMessage,
    isValid,
    phoneValue,
    handlePaste,
    handleDisplayChange,
  };
};
